// Client-driven AHP `createSession` handshake plus the MC-resealed token provider,
// built on the shared protected-resource auth plumbing.

import {AhpClient, RpcError} from '@microsoft/agent-host-protocol/client'
import type {CreateSessionParams as AhpCreateSessionParams, ModelSelection, URI} from '@microsoft/agent-host-protocol'
import {
  AUTH_REQUIRED_CODE,
  type AwpsAuthenticationContext,
  authRequiredResources,
  defaultResourceTokenProvider,
  isGitHubResource,
  satisfyResources,
  type ResourceTokenProvider,
} from '../protected-resource-auth'

const MAX_AUTH_RETRIES = 1

export type CreateSessionAttemptEvent =
  | {phase: 'requested' | 'succeeded'; attempt: number}
  | {phase: 'failed'; attempt: number; errorCode?: number}

/** localStorage key holding the working directory passed to `createSession`. */
export const WORKING_DIRECTORY_STORAGE_KEY = 'ahp_working_directory'

/**
 * Parameters accepted by the AHP `createSession` request.
 *
 * The wire fields are picked from the protocol's own `CreateSessionParams`
 * rather than restated, so an upstream rename (as in the 0.7.0 scalar
 * `workingDirectory` → `workingDirectories` change) fails to compile here
 * instead of silently sending a field the host ignores. Widen the `Pick` when
 * this client starts sending another protocol field.
 *
 * `model` is the one addition: the relay resolves the session's model itself,
 * and the protocol type carries no such field.
 */
export type CreateSessionParams = Pick<
  AhpCreateSessionParams,
  'channel' | 'workingDirectories' | 'config' | 'activeClient'
> & {model?: ModelSelection}

// Web clients hold no raw token; MC reseals it to the host key on /connect. This
// forwards the resealed bearer to resources `isSealableResource` accepts (the
// GitHub host allowlist by default) and falls back to the plaintext provider.
export function sealedGitHubTokenProvider(
  sealedToken: string | undefined,
  fallback: ResourceTokenProvider = defaultResourceTokenProvider,
  isSealableResource: (resource: string) => boolean = isGitHubResource,
): ResourceTokenProvider {
  return resource => {
    if (sealedToken && isSealableResource(resource)) return sealedToken
    return fallback(resource)
  }
}

/** Default working directory, read from localStorage. */
export function defaultWorkingDirectory(): string | undefined {
  try {
    return globalThis.localStorage?.getItem(WORKING_DIRECTORY_STORAGE_KEY) ?? undefined
  } catch {
    return undefined
  }
}

/** Accept a bare filesystem path or a URI; the daemon parses `workingDirectory` as a URI. */
export function toWorkingDirectoryUri(value: string): URI {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return value
  return `file://${value.startsWith('/') ? '' : '/'}${value}`
}

/** Create the session, pushing tokens and retrying once if the host requires authentication. */
export async function createSessionWithAuth(
  client: AhpClient,
  params: CreateSessionParams,
  provider: ResourceTokenProvider = defaultResourceTokenProvider,
  awps?: AwpsAuthenticationContext,
  onAttempt?: (event: CreateSessionAttemptEvent) => void,
): Promise<void> {
  let attempt = 0
  for (;;) {
    onAttempt?.({phase: 'requested', attempt: attempt + 1})
    try {
      await client.request('createSession', params)
      onAttempt?.({phase: 'succeeded', attempt: attempt + 1})
      return
    } catch (err) {
      onAttempt?.({phase: 'failed', attempt: attempt + 1, ...(err instanceof RpcError ? {errorCode: err.code} : {})})
      if (!(err instanceof RpcError) || err.code !== AUTH_REQUIRED_CODE || attempt >= MAX_AUTH_RETRIES) {
        throw err
      }
      const satisfied = await satisfyResources(client, authRequiredResources(err.data), provider, awps)
      if (!satisfied) throw err
      attempt++
    }
  }
}
